import React, { memo, ReactNode, useMemo } from 'react'
import { TableCell, Typography } from '@mui/material'
import LiveMarketText from '../common/LiveMarketText'
import LiveFlashValue from '../common/LiveFlashValue'
import { Security } from '../../proto/generated/security'
import { indexComponentBodyCellSx } from './indexComponentTableStyles'
import { buildIndexComponentLiveSelectors } from './indexComponentLiveDisplay'

export type IndexComponentPriceCellsProps = {
    securityKey: string
    security: Security
    previousClose: number
    spi: number
    initialLtp: number | null
    initialChangePercentage: number | null
    initialContribution: number | null
    ltpCellSuffix?: ReactNode
    showContribution?: boolean
}

const IndexComponentPriceCells = ({
    securityKey,
    security,
    previousClose,
    spi,
    initialLtp,
    initialChangePercentage,
    initialContribution,
    ltpCellSuffix,
    showContribution = true,
}: IndexComponentPriceCellsProps) => {
    const selectors = useMemo(
        () =>
            buildIndexComponentLiveSelectors(securityKey, security, previousClose, spi, {
                ltp: initialLtp,
                changePercentage: initialChangePercentage,
                contribution: initialContribution,
            }),
        [securityKey, security, previousClose, spi, initialLtp, initialChangePercentage, initialContribution]
    )

    return (
        <>
            {showContribution && (
                <TableCell align="right" sx={{ ...indexComponentBodyCellSx, width: '22%' }}>
                    <Typography
                        variant="body2"
                        noWrap
                        align="right"
                        component="div"
                        sx={{ fontSize: '0.8125rem', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                    >
                        <LiveMarketText selectText={selectors.contribution} />
                    </Typography>
                </TableCell>
            )}
            <TableCell align="right" sx={{ ...indexComponentBodyCellSx, width: '20%' }}>
                <Typography variant="body2" noWrap align="right" component="div" sx={{ fontSize: '0.8125rem' }}>
                    <LiveMarketText selectText={selectors.changePercent} selectColor={selectors.ltpColor} />
                </Typography>
            </TableCell>
            <TableCell align="right" sx={{ ...indexComponentBodyCellSx, width: '22%', position: 'relative' }}>
                <Typography
                    variant="body2"
                    noWrap
                    align="right"
                    component="div"
                    sx={{
                        fontFeatureSettings: '"tnum" on,"lnum" on',
                        fontSize: '0.8125rem',
                    }}
                >
                    <LiveFlashValue selectText={selectors.ltp} />
                </Typography>
                {ltpCellSuffix}
            </TableCell>
        </>
    )
}

function priceCellsPropsEqual(prev: IndexComponentPriceCellsProps, next: IndexComponentPriceCellsProps): boolean {
    return (
        prev.securityKey === next.securityKey &&
        prev.previousClose === next.previousClose &&
        prev.spi === next.spi &&
        prev.initialLtp === next.initialLtp &&
        prev.initialChangePercentage === next.initialChangePercentage &&
        prev.initialContribution === next.initialContribution &&
        prev.security.code === next.security.code &&
        prev.security.exchange === next.security.exchange
        && prev.showContribution === next.showContribution
        // The suffix (row actions) is mounted only while the row is hovered; re-render when it appears or disappears.
        && (prev.ltpCellSuffix == null) === (next.ltpCellSuffix == null)
    )
}

export default memo(IndexComponentPriceCells, priceCellsPropsEqual)
